import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, ComboBox, Modal } from "@dealer-center/core-components";
import { ConfigurationPreview } from "./Display/ConfigurationPreview";
import { ProviderSettings } from "./Display/ProviderSettings";
import { Header } from "./Display/Header";
import { CARD_SX } from "./Display/panelStyles";
import { FullBleedPageStyles, PAGE_MARKER } from "./Display/pageLayoutStyles";
import type {
  FieldOption,
  ProviderOption
} from "@dealer-center/ddc-pages-nrt-provider-config-tool-interface";
import {
  buildVehicleJson,
  fieldOptionsFixture,
  providersFixture,
  vehicleIdFixture
} from "./mocks/providerConfigFixture";

const PAGE_TITLE = "Provider Configuration Tool";
const SAVE_SUCCESS_MESSAGE = "Configuration saved successfully!";
const SUCCESS_MESSAGE_DURATION_MS = 3000;

export const PagesNrtProviderConfigToolUi = () => {
  // Nothing here defaults to mock content — every field and the vehicle
  // payload viewer stay disabled/empty until a Provider is picked.
  const [selectedProvider, setSelectedProvider] =
    useState<ProviderOption | null>(null);
  const [selectedField, setSelectedField] = useState<FieldOption | null>(null);
  const [sourceKey, setSourceKey] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  // Baseline Save last persisted, so Field/Provider switches can detect and
  // confirm discarding edits instead of silently overwriting them.
  const [savedSourceKey, setSavedSourceKey] = useState("");
  const [savedDefaultValue, setSavedDefaultValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const isProviderSelected = selectedProvider !== null;
  const hasUnsavedChanges =
    selectedField !== null &&
    (sourceKey !== savedSourceKey || defaultValue !== savedDefaultValue);
  // null = hidden; a save always writes a new number, so a repeat save
  // still restarts the dismiss timer below (an identical string wouldn't).
  const [saveToken, setSaveToken] = useState<number | null>(null);

  // ComboBox re-syncs by reference (.claude/rules/interstate.md).
  const providerValue = useMemo(
    () => (selectedProvider ? [selectedProvider] : []),
    [selectedProvider]
  );

  // Kept in step with the Vehicle ID field.
  const vehicleJson = useMemo(() => buildVehicleJson(vehicleId), [vehicleId]);

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  // Cleared on a timer, so the timeout has to be cancelled on unmount.
  useEffect(() => {
    if (saveToken === null) return undefined;
    const timer = setTimeout(
      () => setSaveToken(null),
      SUCCESS_MESSAGE_DURATION_MS
    );
    return () => clearTimeout(timer);
  }, [saveToken]);

  const handleSaveConfiguration = useCallback(() => {
    setSavedSourceKey(sourceKey);
    setSavedDefaultValue(defaultValue);
    setSaveToken(previous => (previous ?? 0) + 1);
  }, [defaultValue, sourceKey]);

  // Surfaces the picked field's own Source Key/Default so they don't keep
  // showing a stale value left over from whatever field was selected before.
  const applyFieldChange = useCallback((field: FieldOption | null) => {
    setSelectedField(field);
    const nextSourceKey = field?.sourceKey ?? "";
    const nextDefaultValue =
      field?.default != null ? String(field.default) : "";
    setSourceKey(nextSourceKey);
    setDefaultValue(nextDefaultValue);
    setSavedSourceKey(nextSourceKey);
    setSavedDefaultValue(nextDefaultValue);
  }, []);

  const applyProviderChange = useCallback((provider: ProviderOption | null) => {
    setSelectedProvider(provider);
    setSelectedField(null);
    setSourceKey("");
    setDefaultValue("");
    setSavedSourceKey("");
    setSavedDefaultValue("");
    setVehicleId(provider ? vehicleIdFixture : "");
  }, []);

  // Holds the switch to apply once the user confirms discarding unsaved
  // edits; null means no confirmation is pending and the modal stays hidden.
  const [pendingSwitch, setPendingSwitch] = useState<(() => void) | null>(null);

  // Unsaved edits would otherwise be silently overwritten by the new field's
  // own values — confirm before discarding them.
  const handleFieldChange = useCallback(
    (field: FieldOption | null) => {
      if (hasUnsavedChanges) {
        setPendingSwitch(() => () => applyFieldChange(field));
        return;
      }
      applyFieldChange(field);
    },
    [applyFieldChange, hasUnsavedChanges]
  );

  // Unlocks both panels but leaves Field unselected — the user picks that
  // explicitly, and only then does handleFieldChange fill Source Key/Default.
  const handleProviderChange = useCallback(
    (provider: ProviderOption | null) => {
      if (hasUnsavedChanges) {
        setPendingSwitch(() => () => applyProviderChange(provider));
        return;
      }
      applyProviderChange(provider);
    },
    [applyProviderChange, hasUnsavedChanges]
  );

  const handleConfirmDiscard = useCallback(() => {
    pendingSwitch?.();
    setPendingSwitch(null);
  }, [pendingSwitch]);

  const handleCancelDiscard = useCallback(() => setPendingSwitch(null), []);

  // Only Provider Settings fields — searchQuery belongs to the other panel.
  const handleReset = useCallback(() => {
    setSourceKey("");
    setDefaultValue("");
  }, []);

  return (
    <Box
      data-page={PAGE_MARKER}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minWidth: 0,
        width: "100%"
      }}
    >
      <FullBleedPageStyles />
      <Header
        subtitle="Configure your provider settings with ease"
        title={PAGE_TITLE}
      />

      {saveToken !== null && (
        <Alert type="success">{SAVE_SUCCESS_MESSAGE}</Alert>
      )}

      <Box sx={{ ...CARD_SX, gap: 0.5 }}>
        <ComboBox
          label="Provider"
          name="provider"
          options={providersFixture}
          placeholder="Select a provider..."
          value={providerValue}
          // Selection is on `event.target.value`, looked up in the fixture.
          onChange={event => {
            const selectedValue = event.target.value?.[0]?.value;
            handleProviderChange(
              providersFixture.find(p => p.value === selectedValue) ?? null
            );
          }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { md: "1fr 1fr", xs: "1fr" }
        }}
      >
        <ConfigurationPreview
          disabled={!isProviderSelected}
          jsonData={vehicleJson}
          searchQuery={searchQuery}
          vehicleId={vehicleId}
          onSearchChange={setSearchQuery}
          onVehicleIdChange={setVehicleId}
        />

        <ProviderSettings
          defaultValue={defaultValue}
          disabled={!isProviderSelected}
          fieldOptions={fieldOptionsFixture}
          hasUnsavedChanges={hasUnsavedChanges}
          selectedField={selectedField}
          sourceKey={sourceKey}
          onDefaultValueChange={setDefaultValue}
          onFieldChange={handleFieldChange}
          onReset={handleReset}
          onSave={handleSaveConfiguration}
          onSourceKeyChange={setSourceKey}
        />
      </Box>

      <Modal
        footer={{
          primary: { action: handleConfirmDiscard, label: "Discard Changes" },
          options: { action: handleCancelDiscard, label: "Cancel" }
        }}
        header="Discard unsaved changes?"
        show={pendingSwitch !== null}
        size="small"
        onHide={handleCancelDiscard}
      >
        You have unsaved changes to Source Key/Default that will be lost.
      </Modal>
    </Box>
  );
};
